import { Trailer } from "@filmweb/sanity-types";
import { GetServerSideProps } from "next";
import { groq } from "next-sanity";
import Ad from "src/components/ads/Ad";
import StickyBottomAd from "src/components/ads/StickyBottomAd";
import ImageTitle from "src/components/articledisplays/ImageTitle";
import ArticleSoMeRow from "src/components/articlesomerow/ArticleSoMeRow";
import ArticleSummary from "src/components/articlesummary/ArticleSummary";
import Byline from "src/components/byline/Byline";
import ContentWrapper from "src/components/contentwrapper/ContentWrapper";
import InfoBox from "src/components/infobox/InfoBox";
import NewsTopImage from "src/components/newstopimage/NewsTopImage";
import PosterRow from "src/components/posterrow/PosterRow";
import Ingress from "src/components/richtext/Ingress";
import MainText from "src/components/richtext/MainText";
import NewsSeo from "src/components/seo/NewsSeo";
import StreamingSearchBox from "src/components/streaming/StreamingSearchBox";
import StreamingguideLogo from "src/components/streaming/StreamingguideLogo";
import TopOfPageSponsor from "src/components/takoversponsor/TopOfPageSponsor";
import theme from "src/components/themes/DefaultTheme";
import WufooForm from "src/components/wufoo/WufooForm";
import AdWrapper, { AdUnit } from "src/lib/contexts/AdContext";
import { createRichTextQueryFragment } from "src/lib/groq";
import { useOverriddenAdunits } from "src/lib/server/ads";
import { setDefaultCacheHeaders } from "src/lib/server/cache";
import { ICommonProps, IQueryResult, getServerData, getSlugByEceId, isPreview, sanityQuery } from "src/lib/server/sanity";
import styled, { css } from "src/lib/styles/css";
import IFilmklipp from "src/lib/types/Filmklipp";
import IFilmnytt, { DocType_Filmnytt } from "src/lib/types/Filmnytt";
import { DocType_Filmomtale, IFilmomtale } from "src/lib/types/Filmomtale";
import IGallery from "src/lib/types/IGallery";
import { SECTION_ID } from "src/pages/filmnytt";
import { QueryClient, dehydrate } from "@tanstack/react-query";
import { PortableTextBlock } from "sanity";
import IFranchiseBlock from "src/lib/types/IFranchiseBlock";

import { prefetch as prefetchFranchiseBlock } from "src/components/richtext/franchise/FranchiseBlock";
import MailChimpScripts from "src/components/mailchimp/MailChimpScripts";
import JSONLD from "src/components/seo/JSONLD";

//#region [Other]
const PAGE_QUERY: string = groq`
{
	"published": *[_type =="${DocType_Filmnytt}" && slug.current == $slug] {
		...,
		${createRichTextQueryFragment("bodytext")},
		${createRichTextQueryFragment("ingress")},
		author->,
        "topGallery": {
            topGallery->._type == "gallery" => {
                        ...topGallery-> {
                            ...,
                            images[] {
                                ...,
                                asset->
                            }
                        }
                    },
            topGallery->._type == "filmomtale" => {
                ...topGallery-> {
                    ...,
                    mainImageGalleryV2 {
                        ...,
                        asset->
                    },
                    imagesV2[] {
                        ...,
                        asset->
                    },
                    imagesOverrideV2[] {
                        ...,
                        asset->
                    }
                }
            }
        },
		"imageV2": imageV2[]{
            ...,
			asset->
        },
		related[]-> {
			...,
			postersV2[] {
				...,
				asset->
			},
			imageV2[] {
				...,
				asset->
			}
		}
	}[0],
    "mostReadNewsArticleList": *[_id == $sectionId][0].mostReadNewsArticleList[]->
}
`;

//#endregion

interface FilmnyttQueryResult extends IQueryResult<IFilmnytt> {
    mostReadNewsArticleList?: IFilmnytt[];
}

//#region [Other]getServerSideProps
export const getServerSideProps: GetServerSideProps = getServerData(async (context) => {
    const slug = context.params?.slug;
    if (!slug) {
        return { notFound: true };
    }

    // determine if we are in a fulmnytt or streaming context
    let isStreamingContext = /^\/streamingguide\/*/gi.test(context.resolvedUrl);
    const isP = isPreview(context);

    /**
     * "slug" might any of the following:
     * "article:articleId.ece", (Escenic)
     * "article:articleId", (Escenic)
     * ":articleId", (Escenic)
     * slug (sanity)
     *
     * :articleId will always be a plain number, otherwise we can assume it is a sanity slug
     */

    const oldEceMatch = /^(?:article)?(\d+)(?:\.ece)?$/.exec(slug as string);
    if (oldEceMatch) {
        // get the article and send a perma redirect
        console.debug("We are using old ece match", oldEceMatch[1]);
        const mappedSlug = await getSlugByEceId(oldEceMatch[1]);
        if (!mappedSlug) {
            return { notFound: true };
        }

        return {
            redirect: {
                permanent: true,
                destination: isStreamingContext ? `/streamingguide/artikkel/${mappedSlug}` : `/filmnytt/${mappedSlug}`
            }
        };

    }
    const queryResult = await sanityQuery<FilmnyttQueryResult>(PAGE_QUERY, { slug, sectionId: SECTION_ID }, { isPreview: isP });

    const document = queryResult?.published;
    if (!document) {
        return { notFound: true };
    }
    const queryClient = await preloadContent(document);
    isStreamingContext = document?.isStreaming ?? false;
    setDefaultCacheHeaders(context);

    //let adUnits = isStreamingContext ? SG_ADS : FW_ADS;

    let props: any = {
        document,
        isStreamingContext,
        hasCustomBg: false,

        //customBgTargetAdId: "SponsorTrackingPixel",
        mostReadNewsArticleList: queryResult.mostReadNewsArticleList
    }
    if (queryClient) {
        props.dehydratedState = dehydrate(queryClient);
    }

    return {
        props
    };


});

async function preloadContent(document: IFilmnytt): Promise<QueryClient | undefined> {
    const queryClient = new QueryClient();
    const promises: Promise<void>[] = [];
    const franchiseBlockIngressPromise = preloadFranchiseBlock(queryClient, document.ingress);
    if (franchiseBlockIngressPromise) {
        promises.push(franchiseBlockIngressPromise);
    }
    const franchiseBlockBodytextPromise = preloadFranchiseBlock(queryClient, document.bodytext);
    if (franchiseBlockBodytextPromise) {
        promises.push(franchiseBlockBodytextPromise);
    }
    if (promises.length > 0) {
        await Promise.all(promises);
        return queryClient;
    }
}

function preloadFranchiseBlock(queryClient: QueryClient, richText: PortableTextBlock[] | null | undefined): Promise<void> | undefined {
    const franchiseBlocks = (richText?.filter(block => block?._type === "franchiseBlock") as (unknown[] | undefined)) as (IFranchiseBlock[] | undefined);
    if ((franchiseBlocks?.length ?? 0) > 0) {
        const streamingIds = franchiseBlocks!.flatMap(block => {
            return [
                ...(block.franchise.movies?.filter(m => !!m.streamingId)?.map(m => m.streamingId) ?? []),
                ...(block.franchise.series?.filter(m => !!m.streamingId)?.map(m => m.streamingId) ?? [])
            ];
        });

        return prefetchFranchiseBlock(queryClient, { streamingIds });
    }
}

//#endregion

//#region [Props]
export type FilmnyttArticleProps = {
    document: IFilmnytt;
    isStreamingContext: boolean;
    mostReadNewsArticleList?: IFilmnytt[];
} & ICommonProps;
//#endregion




//#region [Component]
export default function FilmnyttArticle({ document, isStreamingContext, appData, mostReadNewsArticleList, adUnits, hasSponsor, sponsorClickUrl, sponsorTopImageDesktop, sponsorTopImageMobile }: FilmnyttArticleProps) {
    const finalAdunits = useOverriddenAdunits(isStreamingContext ? SG_ADS : FW_ADS, adUnits);
    const hasPackshot = finalAdunits.find(adUnit => adUnit.targetId === "FW_bottomSticky")?.isPackshot ?? false;
    // LATER: Consider moving this into NewsTopImage
    const _topImage = (document.imageV2?.length ?? 0 > 0) ? document.imageV2![0] : undefined;
    const _gallery = document.topGallery as (IFilmomtale | IGallery | undefined);
    const _videoDoc = document.related?.find(rel => rel?._type === "trailer" || rel?._type === "filmklipp") as Trailer | IFilmklipp | undefined;

    const relatedMovies = document.related?.filter(related => {
        return related?._type === DocType_Filmomtale;
    }) as IFilmomtale[];

    let relatedNews = document.related?.filter(related => {
        return related?._type === DocType_Filmnytt;
    }) as IFilmnytt[];

    let changeRelatedArticlesHeader = false;
    if ((relatedNews?.length ?? 0) === 0 && mostReadNewsArticleList) {
        relatedNews = mostReadNewsArticleList.slice(0, 3);
        changeRelatedArticlesHeader = true;
    }

    const hideAds = !!document.hideAds || !!document.isAdContent;

    return <>
        <JSONLD document={document} />
        {!hideAds && <SInfoBox messageBanner={appData.settings} />}
        <AdWrapper ads={finalAdunits} defaultProximity={ADPROXIMITY} key={document._id}>
            {isStreamingContext && <>
                {!hasSponsor && <StreamingguideLogo />}

                {hasSponsor && <STopOfPageSponsor sponsorText="streaming" clickUrl={sponsorClickUrl} desktopImages={sponsorTopImageDesktop} mobileImages={sponsorTopImageMobile} />}
                {document.streamingBoxPosition === "top" && <STopSGSearchBox showLogo={false} fullWidth toolbox={appData.streamingToolbox} />}
            </>}

            {!isStreamingContext && hasSponsor && <STopOfPageSponsor hideSponsorTextMobile sponsorText="filmweb" clickUrl={sponsorClickUrl} desktopImages={sponsorTopImageDesktop} mobileImages={sponsorTopImageMobile} />}

            <SFilmnyttArticle>
                <NewsSeo document={document} />
                <ContentWrapper>

                    <NewsTopImage topImage={_topImage} video={_videoDoc} gallery={_gallery} imageText={document.imageTextTopImage} isContentAd={!!document.isAdContent} />

                    <SContentContainer>
                        <SArticleHeader>
                            <SArticleTitle>{document.title}</SArticleTitle>
                            <Byline document={document} settings={appData.settings} isAdContent={document.isAdContent} />
                        </SArticleHeader>
                        <SIngress ingress={document.ingress} />
                        <SArticleSummary summary={document.summary_v2} />
                        {hideAds !== true && <STopAd>
                            <SStickyAd targetId="FW_topp_filmnytt_v2" />
                        </STopAd>}
                        <SMainText mainText={document.bodytext} includeAds={hideAds !== true}>
                            {(!document.streamingBoxPosition || document.streamingBoxPosition === "inline") && <SInlineSGSearchBox maxResults={4} toolbox={appData.streamingToolbox} className="ad" />}
                            <SInlineAd targetId="FW_midt2_filmnytt" className="ad" />
                            <SInlineAd targetId="FW_midt3_filmnytt" className="ad" />
                            {Array.from(Array(NUM_REPEATING_ADS).keys()).map(counter => <SInlineAd key={`RepeatingAd${counter}`} targetId={`FW_midt4_filmnytt_${counter}`} className="ad" />)}
                        </SMainText>

                        {document.wufooFormUrl && <SWufooForm formUrl={document.wufooFormUrl} />}
                        {!document.isAdContent && <SBottomSoMeRow label={appData.settings.articleSoMeLabelBottom?.trim() ?? "Følg oss for siste nytt, konkurranser og film og serietips:"} />}

                    </SContentContainer>

                    {(relatedMovies?.length ?? 0) > 0 && <SRelatedMoviesBlock>
                        <SSubHeading>Relaterte filmer</SSubHeading>
                        <SRelatedPosterRow posterList={relatedMovies} />
                    </SRelatedMoviesBlock>}

                    {(relatedNews?.length ?? 0) > 0 && <aside>
                        <SSubHeading>{changeRelatedArticlesHeader ? <>Meste leste nyheter</> : <>Relaterte nyheter</>}</SSubHeading>
                        <SRelatedArticlesBlock>
                            {relatedNews!.map(doc => <ImageTitle key={doc._id} document={doc} themeValue="light" sizes={`${theme.mq.desktop}: 640px, 100vw`} headerLevel={3} />)}
                        </SRelatedArticlesBlock>
                    </aside>}

                    {!hideAds && <SBottomAd targetId="FW_bunn1_filmnytt" />}

                </ContentWrapper>


                {!hideAds && <SStickyBottomAd isPackshot={hasPackshot} />}



            </SFilmnyttArticle>
        </AdWrapper>
        <MailChimpScripts />
    </>;
}
//#endregion

//#region [Metadata]
const ADPROXIMITY = 0;
const NUM_REPEATING_ADS = 20;

const FW_ADS: AdUnit[] = (() => {
    const result: AdUnit[] = [
        { auId: 'dff45', auW: '100%', auH: '400', targetId: 'FW_topp_filmnytt_v2' },
        { auId: '00000000000e08df', auW: '100%', auH: '200', targetId: 'FW_midt1_filmnytt' },
        { auId: '00000000000e08e1', auW: '100%', auH: '200', targetId: 'FW_midt2_filmnytt' },
        //{ auId: '383176', auW: '100%', auH: '200', targetId: 'FW_midt2_filmnytt' }, // SAN-509
        { auId: '00000000000e08e2', auW: '100%', auH: '200', targetId: 'FW_midt3_filmnytt' },
        //{ auId: '38319b', auW: '100%', auH: '200', targetId: 'FW_midt3_filmnytt' }, // SAN-509
        { auId: '00000000000e08e9', auW: '100%', auH: '300', targetId: 'FW_bunn1_filmnytt' },
        //{ auId: '3831b8', auW: '100%', auH: '300', targetId: 'FW_bunn1_filmnytt' }, // SAN-509
        { auId: '00000000000e08ea', auW: '100%', auH: '300', targetId: 'FW_bunn2_filmnytt' },
        { auId: '00000000001fc39d', auW: '100vw', auH: '15.625vw', targetId: 'FW_bottomSticky', requestMode: "hasTarget" }
    ];

    for (let i = 0; i < NUM_REPEATING_ADS; i++) {
        result.push({ auId: '00000000000e08e5', auW: '100%', auH: '200', targetId: `FW_midt4_filmnytt_${i}` });
        //result.push({ auId: '3831a3', auW: '100%', auH: '200', targetId: `FW_midt4_filmnytt_${i}` }); // SAN-509
    }
    return result;
})();

const SG_ADS: AdUnit[] = (() => {
    const result: AdUnit[] = [
        { auId: '869f1', auW: '100%', auH: '400', targetId: 'FW_topp_filmnytt_v2' },
        { auId: '00000000000869f2', auW: '100%', auH: '200', targetId: 'FW_midt1_filmnytt' },
        { auId: '00000000000e1cb7', auW: '100%', auH: '200', targetId: 'FW_midt2_filmnytt' },
        //{ auId: '383228', auW: '100%', auH: '200', targetId: 'FW_midt2_filmnytt' }, // SAN-509
        { auId: '00000000000e1cb8', auW: '100%', auH: '200', targetId: 'FW_midt3_filmnytt' },
        //{ auId: '383238', auW: '100%', auH: '200', targetId: 'FW_midt3_filmnytt' }, // SAN-509
        { auId: '00000000000869f3', auW: '100%', auH: '300', targetId: 'FW_bunn1_filmnytt' },
        //{ auId: '383240', auW: '100%', auH: '300', targetId: 'FW_bunn1_filmnytt' }, // SAN-509
        { auId: '00000000000e1cba', auW: '100%', auH: '300', targetId: 'FW_bunn2_filmnytt' },
        { auId: '342bdf', auW: '50vw', targetId: 'FW_bottomSticky', requestMode: "hasTarget" },
        //{ auId: '365245', auW: '1px', auH: '1px', targetId: 'SponsorTrackingPixel' },
    ];


    for (let i = 0; i < NUM_REPEATING_ADS; i++) {
        result.push({ auId: '00000000000e1cb9', auW: '100%', auH: '200', targetId: `FW_midt4_filmnytt_${i}` });
        //result.push({ auId: '38323d', auW: '100%', auH: '200', targetId: `FW_midt4_filmnytt_${i}` }); // SAN-509
    }
    return result;
})();
//#endregion

//#region [Styles]
const SInfoBox = styled(InfoBox)`
	margin: ${theme.maxContentPadding};
	width: calc(100% - 2 * ${theme.maxContentPadding});
	@media ${theme.mq.desktop} {
		margin: 20px auto;
		width: auto;
	}

	max-width: ${theme.maxContentWidth}px;
`;

const STopOfPageSponsor = styled(TopOfPageSponsor)`
    .topOfPageSponsorInnerContainer {
        margin: ${theme.maxContentPadding} auto;
        @media ${theme.mq.desktop} {
            margin: 0 auto;
        }
    }
`;

const SFilmnyttArticle = styled.article`
	width: calc(100vw - 20px);
	max-width: calc(100vw - 20px);
	margin: 0 auto;
	color: var(--textcolor);
`;

const SContentContainer = styled.div`
	margin: 0 auto;
	max-width: 1129px;
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	flex-direction: column;
	margin-top: 20px;

	@media ${theme.mq.desktop} {
		margin-top: 35px;
		display: grid;
		grid-template-columns: 2fr 1fr;
		grid-template-rows: auto;
		column-gap: 50px;
	}
`;

const SArticleHeader = styled.header`
	grid-column: 1 / span 1;
	grid-row: 1 / span 1;
	margin-bottom: 25px;
`;

const SArticleTitle = styled.h1`

	font-size: ${theme.fonts.tittelM.size};
	font-weight: ${theme.fonts.tittelM.weight};
	line-height: 40px;
	margin-bottom: 30px; // 40 - lineheight diff

	@media ${theme.mq.desktop} {
		//@media  (orientation: landscape) {
		font-size: ${theme.fonts.tittelXL.size};
		font-weight: ${theme.fonts.tittelXL.weight};
		line-height: 72px;
	}
`;


const CSSText = css`
	a {
		color: var(--inlinelinkcolor);

		&:visited {
			color: var(--inlinevisitedlinkcolor);
		}
	}
	/*
	@media #{settings.$desktop} {//@media  (orientation: landscape) {
		max-width: 755px;
	}*/

	h1,
	h2,
	h3,
	h4 {
		margin-top: 0;
		@media ${theme.mq.desktop} {
			margin-top: 35px;
		}
	}
`;

const SIngress = styled(Ingress)`
	grid-column: 1 / span 1;
	grid-row: 2 / span 1;

	${CSSText}
`;

const SArticleSummary = styled(ArticleSummary)`
    grid-column: 1 / span 1;
    grid-row: 3 / span 1;
    margin-bottom: 35px !important;
    @media ${theme.mq.desktop} {
        margin-bottom: 50px !important;
    }
`;

const SMainText = styled(MainText)`
	grid-column: 1 / span 1;
	grid-row: 4 / span 1;
	margin-bottom: 35px;
	padding-top: 0.1px;

	@media ${theme.mq.desktop} {
		margin-bottom: 25px;
		max-width: 720px;
	}

	p {
		line-height: 1.4;
	}

	${CSSText}
`;

const SWufooForm = styled(WufooForm)`
	grid-column: 1 / span 1;
`;

const SBottomSoMeRow = styled(ArticleSoMeRow)`

    --ICONCOLOR_OVERRIDE: ${theme.palette.lilla4};
    border-radius: ${theme.stdBorderRadius};
    background-color: ${theme.palette.lilla4};
    padding: ${theme.maxContentPadding};
    justify-content: center;
    margin-bottom: 50px;

    .label {
        text-align: center;
        line-height: 1.4;
    }


    @media ${theme.mq.desktop} {
        grid-column: 1 / span 1;
        grid-row: 5 / span 1;
        padding: 30px;

        .label {
            text-align: left;
            line-height: 1;
        }

	}
`;

const SRelatedMoviesBlock = styled.aside`
	margin: 0 10px 40px 10px;

	@media ${theme.mq.desktop} {
		//@media  (orientation: landscape) {
		margin-bottom: 60px;

	}
`;

const SRelatedPosterRow = styled(PosterRow)`
	justify-content: flex-start;
	gap: 1.4%;
	row-gap: 23px;
`;

const SSubHeading = styled.h2`
	margin-bottom: 10px;
`;

const STopAd = styled.div`
	margin: 0 -10px 1em -10px;
	width: 100vw;

	@media ${theme.mq.desktop} {
		//@media  (orientation: landscape) {
		width: 100%;
		height: 100%;
		margin: 0;
		grid-column: 2 / span 1;
		grid-row: 1 / span 4;
	}
`;

const SStickyAd = styled(Ad)`
	width: 100%;
	position: sticky;
	top: 10px;
`;

const SBottomAd = styled(Ad)`
	margin: 10px 0;
	@media ${theme.mq.desktop} {
		margin: 35px 0;
	}
`;

const SInlineAd = styled(Ad)`
	overflow: hidden;

	@media ${theme.mq.mobile} {
		margin: 0 -10px 1em -10px;
	}
`;

const STopSGSearchBox = styled(StreamingSearchBox)`
    margin-bottom: 30px !important;
`;

const SInlineSGSearchBox = styled(StreamingSearchBox)`
	margin-bottom: 1em;
`;

const SRelatedArticlesBlock = styled.div`
	margin: 0 10px 35px 10px;

	display: flex;
	flex-direction: column;
	gap: 10px;

	@media ${theme.mq.desktop} {
		display: grid;
		margin: 0 auto 35px auto;
		gap: 35px;

		// Keep portrait logic
		@media (orientation: portrait) {
			gap: 15px;
		}

		grid-template-columns: repeat(3, 1fr);
		grid-template-rows: auto;
		grid-auto-rows: auto;
	}
`;

const SStickyBottomAd = styled(StickyBottomAd)`
	margin-left: -${theme.maxContentPadding};
`;

//#endregion